/**
 * The weekly quality review's two reads (admin doc 21 §4, product doc 13 §7), under the SLA
 * matrix they measure: who holds what right now, and how the clocks held week by week.
 *
 * A clock still running is neither met nor missed, so each rate shows its own denominator; a
 * backlog of unanswered tickets must not read as a good week.
 */
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Section } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { useAgentSummary, useSlaReport } from '../lib/api/hooks/support';

/** "3 of 4 · 75%", or "none decided" while every clock is still running. */
export function hitRate(met: number, decided: number): string {
  if (decided === 0) return 'none decided';
  return `${met} of ${decided} · ${Math.round((met / decided) * 100)}%`;
}

export function SupportReview() {
  const agents = useAgentSummary();
  const report = useSlaReport();

  return (
    <>
      <Section title="Who holds what" note="Open tickets now, busiest first">
        {agents.isPending && <LoadingState message="Reading the queue…" />}
        {agents.isError && (
          <ErrorState title="Could not read the workload" cause={errorMessage(agents.error)} />
        )}
        {agents.isSuccess && agents.data.items.length === 0 && (
          <EmptyState title="No open tickets" body="Every ticket is resolved or closed." inline />
        )}
        {agents.isSuccess && agents.data.items.length > 0 && (
          <DataTable
            density="compact"
            columns={[
              { key: 'agent', label: 'Agent' },
              { key: 'open', label: 'Open', align: 'right', mono: true },
              { key: 'soon', label: `Due in ${agents.data.due_soon_hours} h`, align: 'right', mono: true },
              { key: 'breached', label: 'Breached', align: 'right', mono: true },
            ]}
            rows={agents.data.items.map((a) => ({
              id: String(a.admin_id ?? 'unassigned'),
              agent: a.admin_id === null ? 'Unassigned' : (a.full_name ?? `#${a.admin_id}`),
              open: a.open,
              soon: a.due_soon,
              breached: a.breached,
            }))}
          />
        )}
      </Section>

      <Section title="How the clocks held" note="By the IST week a ticket was opened">
        {report.isPending && <LoadingState message="Reading the weeks…" />}
        {report.isError && (
          <ErrorState title="Could not read the SLA report" cause={errorMessage(report.error)} />
        )}
        {report.isSuccess && report.data.items.length === 0 && (
          <EmptyState
            title="No tickets"
            body={`Nothing was opened in the last ${report.data.weeks} weeks.`}
            inline
          />
        )}
        {report.isSuccess && report.data.items.length > 0 && (
          <DataTable
            density="compact"
            columns={[
              { key: 'week', label: 'Week of', mono: true },
              { key: 'category', label: 'Category' },
              { key: 'tickets', label: 'Tickets', align: 'right', mono: true },
              { key: 'first', label: 'First response met' },
              { key: 'resolution', label: 'Resolution met' },
            ]}
            rows={report.data.items.map((r) => ({
              id: `${r.week_start}-${r.category}`,
              week: r.week_start,
              category: r.category.replace(/_/g, ' '),
              tickets: r.tickets,
              first: hitRate(r.first_response_met, r.first_response_decided),
              resolution: hitRate(r.resolution_met, r.resolution_decided),
            }))}
          />
        )}
      </Section>
    </>
  );
}
